import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';

// COMPONENTS
import DateRangeSelector from '../../components/common/DateRangeSelector';
import Loader from '../../components/common/Loader';
import Paginator from '../../components/common/Paginator';
import Layout from '../../components/layouts/Layout';
import SingleErrorLog from '../../components/admin/errorLogs/SingleErrorLog';
import AdvancedFiltersContainer from '../../components/common/AdvancedFiltersContainer';

// MESSAGES
import ErrorLogsFormattedMessages from '../../messages/admin/ErrorLogsFormattedMessages';

// REQUESTS
import useErrorLogSearch from '../../requests/ErrorLogsRequest';

// ROUTES
import { AdminRoutes } from '../../routes/AdminRoutes';

// CONFIG
import config from '../../Config';

const Errors = ({ errorHandler, accessLevel, accessType }) => {
  const { searchErrorLog } = useErrorLogSearch();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [pageLength, setPageLength] = useState(config.defaultPL);
  const [selected, setSelected] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectorKeyRefresh, setSelectorKeyRefresh] = useState(false);
  const [filteredText, setFilteredText] = useState([]);
  const [reset, setReset] = useState(false);
  const debounceTimer = useRef(null);

  useEffect(() => {
    if (fromDate && toDate) {
      handleErrorLogSearch(fromDate, toDate);
    }
  }, [fromDate, toDate, filters, reset]);

  useEffect(() => {
    // Generate filter text whenever filters change
    const generateFilterText = () => {
      const filterLabels = {
        logLevelFilter: 'Log Level:',
        logGroupIdFilter: 'Log Group ID:',
        processNameFilter: 'Process:',
        programNameFilter: 'Program:',
        logMessageFilter: 'Message:',
        tagFilter: 'Tag:',
      };

      const activeFilters = Object.entries(filters)
        .filter(([_, value]) => value && value !== '')
        .map(([key, value], i) => (
          <span key={i}>
            {i > 0 ? ', ' : ''}
            <strong>{filterLabels[key]}</strong> {value}
          </span>
        ));

      setFilteredText(activeFilters);
    };

    generateFilterText();
  }, [filters]);

  const handleErrorLogSearch = async (fromDate, toDate, timeRange) => {
    if (fromDate && toDate) {
      try {
        setLoading(true);
        const searchParams = { fromDate, toDate, timeRange, ...filters };
        const response = await searchErrorLog(searchParams, errorHandler);
        setData(response);
        setLoading(false);
      } catch (e) {
        console.error(e);
        setError(e.message);
        setLoading(false);
      }
    }
  };

  const handlePageLengthChange = (event) => {
    setPageLength(parseInt(event.target.value, 10));
    setPageNumber(1);
  };

  const handlePageChange = (newPage) => {
    setPageNumber(newPage);
  };

  const handleFilterClick = (name, value) => {
    // Open the advanced filters panel
    setFilterOpen(true);

    // Update filters state
    setFilters((prev) => ({ ...prev, [name]: value }));

    // Update the corresponding text field
    const form = document.getElementById('log-filter-form');
    if (form) {
      const input = form.elements[name];
      if (input) {
        input.value = value;
      }
    }

    // Clear any existing timer
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    // Set new timer to trigger search after 500ms
    debounceTimer.current = setTimeout(() => {
      setPageNumber(1);
      setReset((prev) => !prev); // Toggle reset to trigger effect
    }, 500);
  };

  if (accessLevel !== '3' || accessType !== '3') return null;

  if (selected)
    return (
      <SingleErrorLog
        errorLog={selected}
        backButtonClick={() => setSelected(null)}
        accessLevel={accessLevel}
        accessType={accessType}
      />
    );

  return (
    <Layout
      routes={AdminRoutes}
      title={
        selected
          ? 'Error Log Details'
          : ErrorLogsFormattedMessages.errorLogsTitle
      }
      accessLevel={accessLevel}
      accessType={accessType}
      backButtonText={selected ? ErrorLogsFormattedMessages.backText : null}
      handleBackButton={() => setSelected(null)}
    >
      <Grid container spacing={2} sx={{ width: '100%' }}>
        <Grid size={12}>
          <DateRangeSelector
            key={selectorKeyRefresh ? 1 : 0}
            handleDateSearch={handleErrorLogSearch}
            parentStartDate={fromDate}
            setParentStartDate={setFromDate}
            parentEndDate={toDate}
            setParentEndDate={setToDate}
          />
          <AdvancedFiltersContainer open={filterOpen} setOpen={setFilterOpen}>
            <Box component="form" id="log-filter-form">
              <Stack
                sx={{
                  flexDirection: { lg: 'row' },
                  gap: 2,
                  marginY: 2,
                  maxWidth: { lg: 674 },
                }}
              >
                <Stack>
                  <Typography sx={{ whiteSpace: 'nowrap', marginTop: 1 }}>
                    Filter Logs:
                  </Typography>
                </Stack>

                <Stack sx={{ gap: 1, flex: 1 }}>
                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Log Level"
                      value={filters.logLevelFilter || ''}
                      onChange={(e) =>
                        handleFilterClick('logLevelFilter', e.target.value)
                      }
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Log Group ID"
                      value={filters.logGroupIdFilter || ''}
                      onChange={(e) =>
                        handleFilterClick('logGroupIdFilter', e.target.value)
                      }
                    />
                  </Stack>

                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Process Name"
                      value={filters.processNameFilter || ''}
                      onChange={(e) =>
                        handleFilterClick('processNameFilter', e.target.value)
                      }
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Program Name"
                      value={filters.programNameFilter || ''}
                      onChange={(e) =>
                        handleFilterClick('programNameFilter', e.target.value)
                      }
                    />
                  </Stack>

                  <Stack
                    sx={{
                      flexDirection: { lg: 'row' },
                      gap: 2,
                      alignItems: { lg: 'center' },
                    }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Log Message"
                      value={filters.logMessageFilter || ''}
                      onChange={(e) =>
                        handleFilterClick('logMessageFilter', e.target.value)
                      }
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Tag"
                      value={filters.tagFilter || ''}
                      onChange={(e) =>
                        handleFilterClick('tagFilter', e.target.value)
                      }
                    />
                  </Stack>
                </Stack>
              </Stack>
            </Box>

            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 2,
                maxWidth: { lg: 674 },
              }}
            >
              {Object.keys(filters).some((key) => filters[key]) && (
                <Button
                  color="secondary"
                  onClick={() => {
                    setFilters({});
                    const form = document.getElementById('log-filter-form');
                    if (form) {
                      form.reset();
                    }
                    setPageNumber(1);
                    setReset((prev) => !prev);
                  }}
                  sx={{
                    textTransform: 'none',
                    textDecoration: 'underline',
                    fontWeight: 400,
                  }}
                >
                  {ErrorLogsFormattedMessages.clear}
                </Button>
              )}
            </Stack>
          </AdvancedFiltersContainer>
        </Grid>
        <Grid size={12}>
          {loading ? (
            <Loader />
          ) : (
            <>
              {data && data.length === 0 ? (
                <Typography>No results found.</Typography>
              ) : (
                <>
                  {filteredText && filteredText?.length > 0 && (
                    <Typography fontSize={12} marginBottom={1}>
                      {filteredText}
                    </Typography>
                  )}
                  <TableContainer
                    sx={{
                      marginBottom: 4,
                      overflowX: 'auto',
                      width: '100%',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Log Time (UTC)</TableCell>
                          <TableCell>Log Level</TableCell>
                          <TableCell>Log Group Id</TableCell>
                          <TableCell>Process Name</TableCell>
                          <TableCell>Program Name</TableCell>
                          <TableCell>Log Message</TableCell>
                          <TableCell>Tag</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.length > 0 &&
                          data
                            .slice(
                              (pageNumber - 1) * pageLength,
                              (pageNumber - 1) * pageLength + pageLength
                            )
                            .map((log, i) => {
                              const logTime = DateTime.fromISO(log.log_time)
                                .toUTC()
                                .toFormat('dd-LLL-yyyy h:mm:ss a');

                              return (
                                <TableRow key={i}>
                                  <TableCell>{logTime}</TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={() =>
                                        handleFilterClick(
                                          'logLevelFilter',
                                          log.log_level
                                        )
                                      }
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { color: 'primary.main' },
                                      }}
                                    >
                                      {log.log_level}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={() =>
                                        handleFilterClick(
                                          'logGroupIdFilter',
                                          log.log_group_id
                                        )
                                      }
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { color: 'primary.main' },
                                      }}
                                    >
                                      {log.log_group_id || 'N/A'}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={() =>
                                        handleFilterClick(
                                          'processNameFilter',
                                          log.process_name
                                        )
                                      }
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { color: 'primary.main' },
                                      }}
                                    >
                                      {log.process_name}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={() =>
                                        handleFilterClick(
                                          'programNameFilter',
                                          log.program_name
                                        )
                                      }
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { color: 'primary.main' },
                                      }}
                                    >
                                      {log.program_name}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Link
                                      tabIndex={0}
                                      onClick={() => setSelected(log)}
                                    >
                                      {log.log_message}
                                    </Link>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component="span"
                                      onClick={() =>
                                        handleFilterClick('tagFilter', log.tag)
                                      }
                                      sx={{
                                        cursor: 'pointer',
                                        '&:hover': { color: 'primary.main' },
                                      }}
                                    >
                                      {log.tag || 'N/A'}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Paginator
                    itemCount={data.length}
                    changeEvent={handlePageChange}
                    pageLength={pageLength}
                    editFunction={handlePageLengthChange}
                    pageNumber={pageNumber}
                    showTotal={'Total Logs'}
                  />
                </>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Layout>
  );
};

export default Errors;
